import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Location } from '@shared/models/location';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MessageService } from '@shared/services/message-service';

import { CustomerService } from '@shared/services/customer.service';

import { Customer } from '@shared/models/customer';
import { CustomerSite } from '@shared/models/customer-site';
import { NormalizedUsAddress } from '@shared/models/normalized-us-address';
import { InitialAddress } from '@shared/components/address-normalizer/address-normalizer.component';
import { DuplicateCheckConfig, DuplicateCheckStatus } from '@shared/components/customer-site-duplicate-list/customer-site-duplicate-list.component';

@Component({
    selector: 'app-add-shipped-to-form',
    templateUrl: './add-shipped-to-form.component.html',
    styleUrls: ['./add-shipped-to-form.component.scss']
})
export class AddShippedToFormComponent implements OnInit, OnDestroy {
    @Input() siteId: string;
    @Input() poolerName: string;
    @Input() customer: Customer;

    private _showDialog: boolean;
    @Input() set showDialog(show: boolean) {
        this._showDialog = show;
        if (show) {
            this.resetFormFromSite(this._updateOf);
        }
    }
    get showDialog(): boolean {
        return this._showDialog;
    }

    selectedNormalizedAddress: NormalizedUsAddress | null = null;
    duplicateCheckConfig: DuplicateCheckConfig | null = null;
    duplicateStatus: DuplicateCheckStatus | null = null;
    selectedDuplicateSite: CustomerSite | null = null;
    private destroyed = new Subject<void>();

    initialAddress: InitialAddress = { streetAddress: '', location: null };

    private _updateOf: CustomerSite | null;

    @Input() set updateOf(val: CustomerSite | null) {
        this._updateOf = val;
    }

    get updateOf(): CustomerSite | null {
        return this._updateOf;
    }

    @Output() showDialogChange = new EventEmitter<boolean>();
    @Output() update: EventEmitter<CustomerSite> = new EventEmitter<CustomerSite>();

    form = new FormGroup({
        locationName: new FormControl<string>('', Validators.required),
        chepGlobalLocationId: new FormControl<string>('')
    });

    working: boolean;

    private _makeGlidRequired: boolean;

    @Input() set makeGlidRequired(value: boolean) {
        this._makeGlidRequired = value;
        this.setGlidValidation();
    }

    get makeGlidRequired(): boolean {
        return this._makeGlidRequired;
    }

    constructor(
        private messageService: MessageService,
        private customerService: CustomerService) {
    }

    // GLID should be required only when makeGlidRequired is true
    setGlidValidation(): void {
        const glidControl = this.form.controls.chepGlobalLocationId;

        if (glidControl) {
            if (this.makeGlidRequired)
                glidControl.setValidators(Validators.required);
            else
                glidControl.clearValidators();

            glidControl.updateValueAndValidity();
        }
    }

    ngOnInit() {
        this.setGlidValidation();
        this.working = false;

        this.form.controls.chepGlobalLocationId.valueChanges.pipe(
            takeUntil(this.destroyed),
            filter(() => !!this.selectedNormalizedAddress)
        ).subscribe(() => {
            this.rebuildDuplicateCheckConfig();
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onAddressSelected(address: NormalizedUsAddress | null): void {
        this.selectedNormalizedAddress = address;
        this.rebuildDuplicateCheckConfig();
    }

    private rebuildDuplicateCheckConfig(): void {
        this.selectedDuplicateSite = null;
        this.duplicateCheckConfig = this.selectedNormalizedAddress && this.customer ? {
            customerId: this.customer.id,
            excludeSiteId: this._updateOf?.id,
            chepGlid: this.form.controls.chepGlobalLocationId.value,
            address: this.selectedNormalizedAddress,
        } : null;
    }

    onDuplicateStatusChange(status: DuplicateCheckStatus): void {
        this.duplicateStatus = status;
    }

    onDuplicateSelected(site: CustomerSite): void {
        this.selectedDuplicateSite = site;
    }

    private resetDuplicateState(): void {
        this.duplicateCheckConfig = null;
        this.duplicateStatus = null;
        this.selectedDuplicateSite = null;
        this.selectedNormalizedAddress = null;
    }

    private resetFormFromSite(site: CustomerSite | null): void {
        this.resetDuplicateState();

        this.form.reset({
            locationName: site?.partySiteName ?? '',
            chepGlobalLocationId: site?.chepGlobalLocationId ?? '',
        }, { emitEvent: false });

        this.initialAddress = {
            streetAddress: site?.address1 ?? '',
            location: new Location(-1, site?.city ?? '', site?.state ?? '', site?.postalCode ?? '')
        };
    }

    get canSave(): boolean {
        return !!this.selectedNormalizedAddress
            && this.duplicateStatus?.loading === false
            && this.duplicateStatus?.sites.length === 0
            && this.form.valid
            && !this.working;
    }

    get canUseSelected(): boolean {
        return !!this.selectedDuplicateSite && !this.working;
    }

    closeShipToLocationDialog(): void {
        this._showDialog = false;
        this.showDialogChange.emit(false);
        this.form.markAsPristine();
        this.resetDuplicateState();
    }

    useSelectedDuplicate() {
        if (!this.selectedDuplicateSite) {
            return;
        }

        this.update.emit(this.selectedDuplicateSite);
        this.closeShipToLocationDialog();
    }

    addShipToLocation() {
        if (!this.customer) {
            this.messageService.add(
                { severity: 'error', summary: 'Could not create Ship To', detail: 'No customer selected.' }
            );
            return;
        }

        if (!this.canSave) {
            this.form.markAllAsTouched();

            if (!this.selectedNormalizedAddress) {
                this.messageService.add(
                    { severity: 'warn', summary: 'Could not create Ship To', detail: 'Please select a normalized address.' }
                );
            } else if (this.form.invalid) {
                this.messageService.add(
                    { severity: 'warn', summary: 'Could not create Ship To', detail: 'Please correct the displayed errors.' }
                );
            }
            return;
        }

        const selectedAddress = this.selectedNormalizedAddress;
        const shipToLocation: CustomerSite = {
            partySiteNumber: '',
            partySiteName: this.form.controls.locationName.value,
            chepGlobalLocationId: this.form.controls.chepGlobalLocationId.value,
            address1: selectedAddress.address1,
            customerId: this.customer.id,
            city: selectedAddress.city,
            postalCode: selectedAddress.postalCode,
            state: selectedAddress.state,
            activeFlag: !this._updateOf,
            isBillTo: this.updateOf?.isBillTo ?? false,
            isShipTo: this.updateOf?.isShipTo ?? false,
            isUpdateOf: this._updateOf?.id,
            isStockTransferSite: this.updateOf?.isStockTransferSite ?? false,
        };

        this.working = true;
        this.customerService.createShipTo(shipToLocation, this.siteId).subscribe({
            next: created => {
                this.working = false;

                const wasUpdate = this._updateOf !== null;
                this._updateOf = created;
                this.shipToLocationCreated(created);

                if (wasUpdate) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Location Update Request Submitted',
                        detail: 'Continue creating/editing the load. The location change will be reviewed by PLA Vision administration.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Shipped To Location Created',
                        detail: 'Save the load to complete changes.'
                    });
                }
                this.closeShipToLocationDialog();
            },
            error: () => {
                this.working = false;
            }
        });
    }

    shipToLocationCreated(customerSiteCreated: CustomerSite) {
        this.update.emit(this._updateOf ?? customerSiteCreated);
    }
}
